import BankAccountFormCst from "../BankAccountFormCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalCst, { OutcomesInterface } from "@components/ModalCst";
import { fetcher } from "src/services/config";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { Box, Container } from "@mui/material";
import { ServicesURL, getAllErrorsAsString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useState, useMemo } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { useAddEditBankAccount } from "../hooks/useAddEditBankAccount";

const EditBankAccount: React.FC = () => {
  const { t } = useTranslation(["accounts-page"]);
  const { idBankAccount, id } = useParams();
  const navigate = useNavigate();

  const { columns, dataDetail, mutate } = useFetchDetail(
    `${ServicesURL.getBankAccounts}`,
    idBankAccount,
    PagesID["accounts"],
  );

  interface Description {
    id: number;
    description: string;
  }

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetBankAccountAssociations,
    costCentersAssociation,
    openAssociations,
  } = useAddEditBankAccount(dataDetail?.dto || []);

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerCostCenter } = useSWRMutation(
    `${ServicesURL.postBankAccounts}/${idBankAccount}`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg }),
  );

  const getInitialOutcome = () => {
    return {
      budget: {
        code: undefined,
        message: t("edit.outcome.bankaccounts.messageLoading"),
        title: t("edit.outcome.bankaccounts.title"),
      },
    };
  };

  const initialOutcome: OutcomesInterface = {
    budget: {
      code: undefined,
      message: t("edit.outcome.bankaccounts.messageLoading"),
      title: t("edit.outcome.bankaccounts.title"),
    },
  };

  const [outcome, setOutcome] = useState<OutcomesInterface>(initialOutcome);
  const [openOutcome, setOpenOutcome] = useState(false);

  const onSubmit = async (inputForm: any) => {
    setOpenOutcome(true);
    const initialOutcomeState: any = getInitialOutcome();
    setOutcome(initialOutcomeState);
    const newOutcome: any = JSON.parse(JSON.stringify(initialOutcomeState));

    const objToSend: any = {
      name: inputForm.name || null,
      sap_number: inputForm.sap_number || null,
      lev_1: inputForm.lev_1 || null,
      lev_2: inputForm.lev_2 || null,
      lev_3: inputForm.lev_3 || null,
      lev_4: inputForm.lev_4 || null,
      lev_5: inputForm.lev_5 || null,
      descriptions: inputForm.descriptions || null,
    };

    try {
      await triggerCostCenter(objToSend);
      newOutcome.budget.code = "OK";
      newOutcome.budget.message = t("edit.outcome.bankaccounts.messageOK");
      setOutcome(newOutcome);
    } catch (error) {
      newOutcome.budget.code = "KO";
      newOutcome.budget.message = getAllErrorsAsString(error);
      setOutcome(newOutcome);
    }
    mutate(inputForm);
  };

  const getColumns = useMemo(() => {
    return columns.map((el) => {
      if (el.accessorKey === "cost_centers") {
        return { ...el, disabled: true };
      } else {
        return { ...el };
      }
    });
  }, []);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-accounts.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("edit.hero-title")}
          description={t("edit.hero-description")}
        />
        {getColumns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <BankAccountFormCst
              row={dataDetail?.dto || []}
              columns={getColumns}
              onSubmit={onSubmit}
              setOpenAssociation={(accessorKey, open) =>
                setOpenAssociation(accessorKey, open)
              }
              handleAssociation={(accessorKey, value) =>
                handleAssociation(accessorKey, value)
              }
              handleDelete={(accessorKey, value) =>
                handleDelete(accessorKey, value)
              }
              resetBankAccountAssociations={resetBankAccountAssociations}
              getValues={(accessorKey) => getValues(accessorKey)}
              association={openAssociations}
              backButton={() => {
                navigate(-1);
              }}
              fullWidth={true}
            />
          </Box>
        )}
      </Container>
      <ModalCst
        open={openOutcome}
        setOpen={setOpenOutcome}
        typeOfModal={"outcome"}
        data={{
          outcome: outcome,
          callBackSubmit: () => {
            setOpenOutcome(!openOutcome);
            navigate(
              id
                ? `/corporate/${id}/detail/budget-management/setup/accounts`
                : `/budget-management/setup/accounts`,
            );
          },
        }}
      />
    </AnimationFadeIn>
  );
};

export default EditBankAccount;
